/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { Slider } from "@/components/atoms/slider";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SwiperCore from "swiper";
import { Navigation } from "swiper/modules";
import { SwiperSlide } from "swiper/react";
import styles from "./styles.module.css";

import { Badge } from "@/components/atoms/badge";
import { BrandFormatByItemName } from "@/constants";
import {
  AmenityServicesQuery,
  Brand,
  ComponentJapanWeather,
  HotelBrandsQuery,
  SetOfTag,
  UploadFile,
} from "@/types/generated";
import { formatPrice, HotelListSelected, TYPE_GROUP } from "@/utils";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";
import { SelectOptionValue } from "../hotel-result";
import { SearchAvailableRoom } from "../search-avaliable/FilterAvailableRoom";
import HotelItem from "./HotelItem";
import { IBanner } from "../hotel-result/banner/BannerBrand";

type THotelBrands = HotelBrandsQuery["hotelBrands"];

export type TBrandLogo = {
  documentId?: string;
  name?: string | null;
  cloudflare_url?: string | null;
  url?: string | null;
};
// Using `any` here is intentional as the function handles dynamic and highly customizable data structures.
export type THotelData = {
  documentId: string | undefined;
  name: string | undefined;
  reviewCount: string | null | undefined;
  rating: string | null | undefined;
  city: string | null | undefined;
  description: string | null | undefined;
  image: string | null | undefined | UploadFile;
  brand: any;
  service: any;
  btnText: string | null | undefined;
  benefits: string[] | null | undefined;
  lat?: string | null;
  lng?: string | null;
  nearByStation?: boolean;
  slug?: string | null;
  withHotSprings?: boolean;
  externalUrl?: string | null;
  triplaCode?: string | null;
  minPrice?: number | null;
  weather?: ComponentJapanWeather;
  brandDocId?: string;
  triplaId?: number | string;
  brandImage?: string | null;
  brandItemName?: string | null;
  triplaBookingCode?: string | null;
  currentTag?: SetOfTag;
};

export interface IHotelListSliderProps {
  data: THotelData[];
  hotelBrandData?: THotelBrands;
  brandLogoData?: TBrandLogo[];
  amenitiesAndServiceData?: AmenityServicesQuery;
  itemPerSlide?: number;
  sectionTitle?: string;
  revealEdge?: boolean;
  location?: string;
  sliderId?: string;
  valueSearch?: SelectOptionValue | null;
  typeGroup?: TYPE_GROUP;
  filterState?: SearchAvailableRoom | null;
  currentBrand?: HotelBrandItemNullable;
  currentBrandLogo?: Brand;
  hotelsWithPrice?: { triplaCode: number; minPrice: number }[];
  brandBannerList?: IBanner[];
  locationId?: string | null;
  locationItemName?: string | null;
  isHotelListPage?: boolean;
  selectedHotels?: HotelListSelected[] | null;
  isFirstCityChange?: React.MutableRefObject<boolean>;
  scale?: number;
  hotelCount?: number;
  seeMoreText?: string;
  searchResultFieldText?: string;
  isHotelOnSenPage?: boolean;
  currentTag?: SetOfTag;
  priority?: boolean;
}

const CHUNK_SIZE = 12;
/**
 * HotelListSlider Component
 *
 * Displays a list of hotels inside a responsive Swiper-based slider.
 * Handles dynamic scaling, spacing, filtering, and "see more" pagination.
 */

export default function HotelListSlider(
  props: Readonly<IHotelListSliderProps>,
) {
  const {
    data,
    itemPerSlide = 4,
    sectionTitle = "",
    revealEdge = true,
    amenitiesAndServiceData,
    sliderId,
    filterState,
    currentBrand,
    typeGroup,
    hotelsWithPrice,
    brandBannerList,
    isHotelListPage,
    isFirstCityChange,
    scale = 1,
    hotelCount,
    seeMoreText,
    searchResultFieldText,
    isHotelOnSenPage,
    currentTag,
    priority = false
  } = props;
  const [mounted] = useState(true);
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const [spaceBetween, setSpaceBetween] = useState(20);
  const [slidesOffset, setSlidesOffset] = useState(0);
  const [firstSlideWidth, setFirstSlideWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!slideRef.current) return;

    // Batch DOM read inside requestAnimationFrame to avoid forced reflow
    requestAnimationFrame(() => {
      if (!slideRef.current) return;
      
      const realWidth = slideRef.current.offsetWidth;
      const newSpace =
        scale >= 1 ? 20 : -Math.round((1 - scale) * realWidth) + 15;
      const newOffset = scale < 1 ? newSpace / 2 : 0;

      // Avoid unnecessary re-renders by updating only when values differ
      if (spaceBetween !== newSpace || slidesOffset !== newOffset) {
        setSpaceBetween(newSpace);
        setSlidesOffset(newOffset);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  const visibleData = useMemo(
    () => data.slice(0, visibleCount),
    [data, visibleCount],
  );
  useLayoutEffect(() => {
    // Batch DOM read inside requestAnimationFrame to avoid forced reflow
    requestAnimationFrame(() => {
      if (slideRef.current) {
        setFirstSlideWidth(slideRef.current.offsetWidth);
      }
    });
  }, [visibleData, scale]);
  // Compute section title based on context
  const sliderLabel =
    isFirstCityChange &&
    isFirstCityChange.current &&
    filterState?.hotelOrCity?.type === "city" &&
    isHotelListPage
      ? filterState?.hotelOrCity?.label + "のホテル"
      : sectionTitle;

  useEffect(() => {
    if (!swiperInstance) return;
    swiperInstance.update();
    swiperInstance.slideTo(0);
  }, [data, swiperInstance]);
  // Resolve correct brand image from brand banner list
  const getBrandImage = useCallback(
    (brandItemName: string) => {
      const brandName = brandBannerList?.find((i) => {
        const itemName = i?.itemName;
        if (!itemName) return false;
        return (
          itemName === brandItemName ||
          BrandFormatByItemName[itemName] === brandItemName
        );
      });
      return brandName?.brandLogoUrl || brandName?.brandDesktopUrl || "";
    },
    [brandBannerList],
  );
  // Extract and sort hotel service icons
  const getServices = useCallback(
    (serviceData: { documentId: string; order: number }[]) => {
      const serviceIds = (serviceData || [])?.map((d) => d.documentId);

      const iconItems =
        amenitiesAndServiceData?.contentAmenitiesAndServices?.[0]?.custom.filter(
          (i) => serviceIds.includes(i?.documentId as string),
        ) ?? [];
      if (iconItems?.length === 0) return [];
      const cssClasses =
        amenitiesAndServiceData?.iconGroups?.flatMap(
          (item) => item?.css_classes ?? [],
        ) ?? [];
      const mergedIcons = iconItems?.map((item) => {
        const cssClass = (cssClasses || [])?.find(
          (data) => item?.side_popup_icon?.[0]?.documentId === data?.documentId,
        );
        return {
          item_name: item?.item_name,
          title: item?.title,
          documentId: item?.documentId,
          side_popup_icon: cssClass?.properties?.css_class || null,
          order: (serviceData || [])?.find(
            (d) => d?.documentId === item?.documentId,
          )?.order,
        };
      });
      return mergedIcons.sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    [amenitiesAndServiceData],
  );

  const chunkedRawData = useMemo(() => {
    const start = 0;
    const end = start + CHUNK_SIZE;
    return data.slice(start, end);
  }, [data]);

  const showNavigation = data?.length > itemPerSlide;

  // Combine all computed hotel data into mapped list for rendering
  const mappedData = useMemo(() => {
    if (!chunkedRawData) return [];

    return visibleData.map((item) => {
      const currentPrice = hotelsWithPrice?.find(
        (data) => data.triplaCode.toString() === item.triplaCode,
      );
      return {
        name: item?.name,
        reviewCount: item?.reviewCount,
        rating: item?.rating,
        city: item?.city,
        description: item?.description,
        image: item?.image as UploadFile,
        brandImageUrl: item?.brandItemName
          ? getBrandImage(item?.brandItemName)
          : "",
        service: item?.service ? getServices(item.service) : null,

        price:
          currentPrice && currentPrice.minPrice > 0
            ? `¥${formatPrice(currentPrice.minPrice)}~`
            : "",
        btnText: item.btnText,
        slug: item.slug,
        externalUrl: item?.externalUrl,
        weather: item?.weather,
        brandDocId: item?.brandDocId,
      };
    });
  }, [
    visibleData,
    hotelsWithPrice,
    chunkedRawData,
    getBrandImage,
    getServices,
  ]);

  const hasMore = visibleCount < data?.length;

  // Update Swiper when new chunk is loaded
  useEffect(() => {
    if (!swiperInstance) return;
    swiperInstance.update();
    swiperInstance.slideTo(0);
  }, [chunkedRawData?.length, swiperInstance]);

  const handleSeeMore = () => {
    setVisibleCount((prev) => Math.min(prev + CHUNK_SIZE, data?.length));
  };
  const isShowTitle =
    filterState?.hotelOrCity?.type !== "hotel" ||
    (filterState?.hotelOrCity?.type === "hotel" &&
      isFirstCityChange &&
      !isFirstCityChange.current);

  return (
    <div className="mb-10 w-full">
      {/* Header with navigation */}
      <div className="mb-3 flex flex-col">
        <div className="flex items-center justify-center lg:justify-between">
          <div className="flex w-full flex-col gap-2 pl-0 md:pl-5">
            <div className="flex w-auto flex-col items-start gap-1 md:w-full md:flex-row md:items-center lg:gap-2">
              {isShowTitle && (
                <div className="w-full md:w-auto">
                  <h2
                    id="section-title"
                    className="text-primary-950 m-0 text-center text-2xl font-[var(--font-semibold)] break-words whitespace-normal md:w-auto md:text-left md:text-3xl"
                  >
                    {sliderLabel}
                  </h2>
                </div>
              )}
              <div className="w-full md:w-auto">
                {typeof hotelCount == "number" && (
                  <div className="text-center text-[var(--color-gray-700)] md:w-auto md:text-left">
                    {hotelCount === 0
                      ? "0件が見つかりました"
                      : hotelCount +
                        ` ${searchResultFieldText || "件が見つかりました"}`}
                  </div>
                )}
              </div>
            </div>
          </div>
          {showNavigation && (
            <div className="mb-3 hidden items-center gap-10 lg:flex">
              <div className="flex items-center gap-3">
                <div
                  className={`swiper-button-prev prev-${sliderId} ${styles.navButton} flex aspect-square !w-10 cursor-pointer items-center justify-center rounded-full bg-white shadow-md`}
                ></div>
                <span className="text-primary-950 min-w-15 font-[var(--font-medium)]">
                  前へ
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-primary-950 min-w-15 font-[var(--font-medium)]">
                  次へ
                </span>
                <div
                  className={`swiper-button-next next-${sliderId} ${styles.navButton} flex aspect-square !w-10 cursor-pointer items-center justify-center rounded-full bg-white shadow-md`}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Active filter badges (brand, preferences, etc.) */}
        {typeGroup === TYPE_GROUP.BASIC && (
          <div className="hide-scrollbar flex items-center gap-2 overflow-x-auto">
            <div className="flex min-w-max flex-nowrap items-center gap-2">
              {(!!currentBrand || isHotelOnSenPage || !!currentTag) &&
                filterState?.hotelOrCity &&
                filterState?.hotelOrCity.type === "city" && (
                  <Badge className={"flex-shrink-0 cursor-default"}>
                    {filterState?.hotelOrCity.label}
                  </Badge>
                )}
              {[
                ...(filterState?.brands || []),
                ...(filterState?.preferences || []),
              ].map((item, index) => (
                <Badge
                  key={`${item.label}-${index}`}
                  className={"flex-shrink-0 cursor-default"}
                >
                  {item.label}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        <div className={`${styles.sliderWrapper}`}>
          <div className={`pb-5 ${revealEdge ? "pr-[50px]" : ""}`}>
            {mounted && (
              <Slider
                id={`slider-${sliderId}`}
                slidesPerGroup={1}
                modules={[Navigation]}
                spaceBetween={spaceBetween}
                slidesOffsetBefore={slidesOffset}
                slidesOffsetAfter={slidesOffset}
                slidesPerView={itemPerSlide}
                navigation={
                  showNavigation
                    ? {
                        nextEl: `.next-${sliderId}`,
                        prevEl: `.prev-${sliderId}`,
                      }
                    : false
                }
                breakpoints={{
                  320: { slidesPerView: 1 },
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                  1280: { slidesPerView: itemPerSlide },
                }}
                className={styles.swiperContainer}
                onSwiper={(sw) => {
                  setSwiperInstance(sw);
                }}
              >
                {mappedData.map((slide, idx) => {
                  const isLastVisible = idx === mappedData.length - 1;
                  const mappedDataSwiperSlideKey = `mapped-data-swiper-slider-${idx}`;
                  return (
                    <SwiperSlide
                      key={mappedDataSwiperSlideKey}
                      className="group relative overflow-visible"
                      style={
                        scale < 1
                          ? {
                              width: `${100 / itemPerSlide}%`,
                              padding: 0,
                              margin: 0,
                            }
                          : {}
                      }
                    >
                      <div
                        style={
                          scale < 1
                            ? {
                                transform: `scale(${scale})`,
                                transformOrigin: "top center",
                                width: "100%",
                              }
                            : {}
                        }
                        ref={idx === 0 ? slideRef : undefined}
                      >
                        <HotelItem
                          image={slide.image}
                          city={slide.city || ""}
                          name={slide.name || ""}
                          rating={Number.parseFloat(slide.rating || "")}
                          price={String(slide.price)}
                          reviewCount={slide.reviewCount || "0"}
                          showMoreBtnText={slide.btnText}
                          brandImageUrl={slide.brandImageUrl}
                          serviceIcons={slide.service}
                          slug={slide.slug}
                          weather={slide.weather}
                          externalUrl={slide.externalUrl}
                          priority={priority && idx === 0}
                          className="transition-all duration-200 ease-in-out group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:scale-104"
                        />
                      </div>
                      {hasMore && isLastVisible && (
                        <button
                          onClick={handleSeeMore}
                          className="absolute !z-[100] flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white font-[var(--font-semibold)] text-[var(--text-base-primary)] shadow-xl"
                          style={{
                            top: scale < 1 ? `${50 * scale}%` : "50%",
                            right:
                              scale < 1 && firstSlideWidth
                                ? `${-40 + (firstSlideWidth * (1 - scale)) / 2}px`
                                : "-50px",
                            width: scale < 1 ? `${100 * scale}px` : "100px",
                            height: scale < 1 ? `${100 * scale}px` : "100px",
                            fontSize: scale < 1 ? `${14 * scale}px` : "14px",
                          }}
                        >
                          {seeMoreText || "もっと見る"}
                        </button>
                      )}
                    </SwiperSlide>
                  );
                })}
              </Slider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}